/**
 * Aircraft Custodian API client + mapper (verified against the live ELB
 * 2026-08-20, after BE reshaped both endpoints).
 *
 * The list endpoint now returns the FULL joined record — Custodian config +
 * live dTwin state + the imported DroneLogbook aircraft row (brand, model,
 * type, callsign, weight, speeds, notes…). One call populates both the
 * drones list and most of the detail panel.
 *
 * `/config` is now a FLAT object (it used to nest `adsb` / `oi_defaults` /
 * `adapter_config`). It adds the handful of fields the list omits:
 * `host`/`port`/`protocol`, `c2_links`, `video_feeds`, `surveillance`,
 * `tc_reg`. Reading the old nested shape threw a TypeError and took the
 * whole detail panel down, so both raw interfaces below are pinned to the
 * verified live shape.
 *
 * Plain-HTTP + no CORS → we go through the same-origin `/custodian-api`
 * proxy (vite dev / worker.ts), which injects the X-Tenant-Id header
 * server-side.
 */
import { normaliseAdapter } from '@/lib/api/adapterTypes';
import { config } from '@/lib/config';
import type { DaaRule, DataSourceMonitor, DjiStatus, InventoryDrone, InventoryUser } from './types';

/** Raw record shape as returned by `GET /api/v1/drones` — verified live
 *  2026-08-20 across all 27 stage aircraft.
 *
 *  DLB-sourced numerics arrive as STRINGS (`"20"`, `"0"`, `"21600"`), so
 *  every one of them goes through `numStr()` rather than being used raw.
 *  `adapter_config` is `{}` on every current drone and appears to be
 *  superseded by the flat `host`/`port`/`protocol` on `/config`; it's still
 *  read defensively as a fallback. */
export interface CustodianDrone {
  tenant_id: string;
  tenant_name?: string;
  serial_number: string;
  dlb_guid: string;
  adapter_type: string;
  adapter_config?: Record<string, unknown> | null;
  /** Flat connection protocol on the list record (e.g. `mavlink_v2`) — the same
   *  value `/config` carries; `adapter_config` is empty on every current drone. */
  protocol?: string | null;
  publish_rate_hz: number;
  /** Added to the list endpoint after the 2026-08-20 shape was verified —
   *  not in the original doc comment above, but present on every live
   *  record since. Optional here defensively; `toInventoryDrone` falls
   *  back to 'none' when absent. */
  gcs_plugin?: string | null;
  gcs_plugin_status?: string | null;
  /** ENG-263 — also on the list record; `fh2 | dji_cloud_api | dronesense…`. */
  data_source_mechanism?: string | null;
  data_source_status?: string | null;
  /** ENG-263 (Custodian 1.0.15) — derived link / telemetry / dock block. */
  live_status?: {
    link?: {
      mechanism?: string | null;
      state?: string | null;
      last_seen_at?: string | null;
    } | null;
    telemetry?: {
      last_telemetry_at?: string | null;
      /** ENG-303 — `networkid_streaming_disabled` when the drone's telemetry is
       *  switched off; `null` otherwise. Always sent on the MQTT status message;
       *  not confirmed on this REST record, so read defensively. */
      reason?: string | null;
    } | null;
    dock?: {
      name?: string | null;
      online?: boolean | null;
      aircraft_state?: string | null;
    } | null;
  } | null;

  // ── Custodian config ──
  oi_automation_enabled: boolean;
  horizontal_speed_ms: number;
  vertical_speed_ms: number;
  /** Renamed from `flight_volume_m` (2026-08). Lateral FV buffer, metres. */
  fv_buffer_m: number | null;
  /** Renamed from `contingency_vol_m` (2026-08). Lateral CV buffer, metres. */
  cv_buffer_m: number | null;
  waypoint_buffer_m: number | null;
  /** ENG-294/295 (Custodian 1.0.22): per-aircraft keep-out buffers, metres, >= 0.
   *  `null` = not set. Absent on older images, hence optional. */
  geofence_buffer_m?: number | null;
  obstacle_buffer_m?: number | null;
  oi_automation_square_m: number | null;
  oi_automation_radius_m: number | null;
  oi_automation_amend_min_trigger_m: number | null;
  oi_automation_amend_max_alt_agl_m: number | null;
  oi_automation_amend_enabled: boolean;
  /** ENG-286 — absent on older Custodian images. */
  oi_automation_time_amend_enabled?: boolean | null;
  oi_automation_time_amend_trigger_s?: number | null;
  oi_automation_time_amend_extension_s?: number | null;
  adsb_in_enabled: boolean;
  adsb_out_enabled: boolean;
  adsb_surrogate_enabled: boolean;
  tisb_rebroadcast_enabled: boolean;
  /** ENG-305 (Custodian 1.0.18) — ADS-B Flight ID / callsign, e.g. `AIRTL25`.
   *  Not the ICAO address (`adsb_hex`). `null` = not set; absent on older images. */
  adsb_identification_code?: string | null;
  /** ENG-303 (Custodian 1.0.20) — telemetry on/off for this drone; absent on older images. */
  networkid_streaming_enabled?: boolean | null;
  ownership_flag_enabled: boolean;

  // ── Live dTwin state ──
  position_lat: number | null;
  position_lon: number | null;
  position_alt: number | null;
  flight_mode: string | null;
  armed: boolean | null;
  battery_remaining_pct: number | null;
  link_rssi_dbm: number | null;
  adapter_connected: boolean;
  last_telemetry_at: string | null;

  keycloak_client_id: string | null;
  client_id?: string | null;
  aadms_id: string | null;
  retired_at: string | null;
  created_at?: string;
  updated_at?: string;
  imported_at?: string;

  // ── Imported DroneLogbook aircraft row ──
  guid?: string;
  name?: string | null;
  callsign?: string | null;
  identification_number?: string | null;
  brand?: string | null;
  drone_model?: string | null;
  drone_type?: string | null;
  status?: string | null;
  color?: string | null;
  firmware_version?: string | null;
  hardware_version?: string | null;
  flight_controller_serial_number?: string | null;
  controller_serial_number?: string | null;
  controller_serial_number2?: string | null;
  max_horizontal_speed?: string | number | null;
  max_vertical_speed?: string | number | null;
  max_flight_time?: string | number | null;
  payload_capacity?: string | number | null;
  weight?: string | number | null;
  insurable_value?: string | number | null;
  propulsion_type?: string | null;
  purchase_date?: string | null;
  tech_number?: string | null;
  company_guid?: string | null;
  user_guid?: string | null;
  notes?: string | null;
}

/** One entry of `drones.drones.daa_rules` (jsonb array).
 *
 *  Deliberately open: per the 2026-08-27 spec the server accepts "any JSON
 *  object describing the rule" and only guarantees the generated `id`. The
 *  four named keys below are the ones the mockup's rule cards render when
 *  present — they are hints for the UI, not a contract. Anything else the
 *  author sent is preserved on the index signature so a round-trip through
 *  the client never silently drops fields. */
export interface DaaRuleRecord {
  id?: string;
  condition?: string;
  action?: string;
  priority?: string;
  enabled?: boolean;
  [key: string]: unknown;
}

/** Raw record shape as returned by `GET /api/v1/drones/{serial}/config` —
 *  verified live 2026-08-21 after BE widened it from 34 to 60 fields. FLAT,
 *  not nested.
 *
 *  It is now the single richest per-drone endpoint: it carries the OI
 *  geometry and TIS-B flag the list also has, PLUS the pilot, separation,
 *  data-source, ADS-B-device, DAA and custodian-runtime fields that had no
 *  home before. Many are still `null` for every aircraft — the columns
 *  exist, nothing populates them yet — so read all of them defensively.
 *
 *  `surveillance` was DROPPED in the same change; `surveillance_mode` now
 *  has no source and renders '—'. */
export interface CustodianDroneConfig {
  serial_number: string;
  callsign: string | null;
  tc_reg: string | null;
  dlb_guid: string;
  adapter_type: string;
  adapter_config?: Record<string, unknown> | null;
  publish_rate_hz: number;

  // ── OI automation ──
  oi_automation_enabled: boolean;
  oi_automation_square_m: number | null;
  oi_automation_radius_m: number | null;
  oi_automation_amend_min_trigger_m: number | null;
  oi_automation_amend_max_alt_agl_m: number | null;
  oi_automation_amend_enabled: boolean | null;
  /** ENG-286 — absent on older Custodian images. */
  oi_automation_time_amend_enabled?: boolean | null;
  oi_automation_time_amend_trigger_s?: number | null;
  oi_automation_time_amend_extension_s?: number | null;
  horizontal_speed_ms: number;
  vertical_speed_ms: number;
  fv_buffer_m: number | null;
  cv_buffer_m: number | null;
  waypoint_buffer_m: number | null;
  /** ENG-294/295 (Custodian 1.0.22): per-aircraft keep-out buffers, metres, >= 0.
   *  `null` = not set. Absent on older images, hence optional. */
  geofence_buffer_m?: number | null;
  obstacle_buffer_m?: number | null;

  // ── Toggles ──
  /** Present in the response but NOT writable — a PUT containing it is
   *  accepted and silently ignored (verified 2026-08-21). Read-only until BE
   *  wires it up. */
  networkid_streaming_enabled: boolean | null;
  tisb_rebroadcast_enabled: boolean;
  adsb_surrogate_enabled: boolean;
  /** Read-only here — written via `PUT /ownership`, which also takes the
   *  `reason` this endpoint has no field for. */
  ownership_flag_enabled: boolean;

  // ── Pilot & separation. BE split the two `*_distance_m` scalars into the
  //    eight crewed/uncrewed x horizontal/vertical columns the reference
  //    mockup always showed (API update 2026-08-27). The old scalars are
  //    gone from the response but kept optional below so a tenant still on
  //    the pre-split build degrades to them rather than rendering '—'. ──
  default_pilot_name: string | null;
  default_pilot_fs_guid: string | null;
  nmac_crewed_h: number | null;
  nmac_crewed_v: number | null;
  nmac_uncrewed_h: number | null;
  nmac_uncrewed_v: number | null;
  swc_crewed_h: number | null;
  swc_crewed_v: number | null;
  swc_uncrewed_h: number | null;
  swc_uncrewed_v: number | null;
  /** @deprecated Pre-2026-08-27 shape. Read-only fallback, never written. */
  nmac_distance_m?: number | null;
  /** @deprecated Pre-2026-08-27 shape. Read-only fallback, never written. */
  swc_distance_m?: number | null;
  eval_distance_m: number | null;

  // ── Data-source monitoring. Only `max_latency_ms` is writable; the rest
  //    are BE-populated observations (all null today). ──
  data_source_status: string | null;
  data_source_mechanism: string | null;
  data_source_rate_hz: number | null;
  data_source_latency_ms_p50: number | null;
  data_source_latency_ms_p95: number | null;
  data_source_last_seen_at: string | null;
  data_source_max_latency_ms: number | null;

  // ── Conspicuity. The two `*_enabled` flags are writable; device/hex and
  //    the observed block are read-only. ──
  adsb_in_enabled: boolean;
  adsb_out_enabled: boolean;
  adsb_out_device: string | null;
  adsb_hex_mode: string | null;
  adsb_hex: string | null;
  /** ENG-305 (Custodian 1.0.18) — see the list record. Optional: older images omit it. */
  adsb_identification_code?: string | null;
  /** NOT listed in the 2026-08-27 field spec. Optional because we can't tell
   *  from the doc whether it was dropped or merely omitted from the writeup —
   *  BE question outstanding. Read defensively either way. */
  adsb_observed?: {
    broadcasting?: boolean;
    last_icao_hex?: string | null;
    last_seen_by_cifib?: string | null;
  } | null;

  /** Free-form rule objects — the 2026-08-27 spec guarantees only `id` (the
   *  server stamps it; a client-supplied one is overwritten). Every other key
   *  is whatever the rule author put there, so nothing here is assumed. Write
   *  via the dedicated DAA endpoints, never `PUT /config`. */
  daa_rules: DaaRuleRecord[] | null;

  // ── Custodian runtime ──
  /** Writable as of 2026-08-27. */
  gcs_plugin: string | null;
  /** Writable as of 2026-08-27. */
  gcs_plugin_status: string | null;
  /** Not listed in the 2026-08-27 field spec — see `adsb_observed`. */
  custodian_deployed?: boolean | null;
  /** Not listed in the 2026-08-27 field spec — see `adsb_observed`. */
  custodian_configured?: boolean | null;
  /** Not listed in the 2026-08-27 field spec — see `adsb_observed`. */
  custodian_runtime?: string | null;

  host: string | null;
  port: number | null;
  protocol: string | null;
  c2_links: number | null;
  video_feeds: number | null;

  // ── Aircraft record ──
  status: string | null;
  type: string | null;
  brand: string | null;
  model: string | null;
  firmware: string | null;
  hardware: string | null;
  weight: string | number | null;
  payload_capacity: string | number | null;
  max_horizontal_speed: string | number | null;
  max_vertical_speed: string | number | null;
  max_flight_time: string | number | null;
  insurable_value: string | number | null;
}

interface DronesResponse {
  drones: CustodianDrone[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchCustodianDrones(): Promise<InventoryDrone[]> {
  // The API caps `limit` at 100, so page through until we have the full fleet
  // (`total`). Guard the loop at 100 pages (10k drones) as a safety backstop.
  const limit = 100;
  const all: CustodianDrone[] = [];
  for (let page = 1; page <= 100; page++) {
    const res = await fetch(`${config.custodian.base}/api/v1/drones?page=${page}&limit=${limit}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Custodian API ${res.status}`);
    const json = (await res.json()) as DronesResponse;
    const batch = json.drones ?? [];
    all.push(...batch);
    if (batch.length === 0 || all.length >= (json.total ?? all.length)) break;
  }
  return all.map(toInventoryDrone);
}

/** `GET /drones/{serial}` — one aircraft, same record shape as a list entry
 *  (verified live 2026-08-20). Used to reconcile a single drone after a save
 *  instead of re-paging the whole fleet. */
export async function fetchCustodianDrone(serial: string): Promise<InventoryDrone> {
  const res = await fetch(`${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Custodian API ${res.status}`);
  return toInventoryDrone((await res.json()) as CustodianDrone);
}

/** `GET /config` — per-drone, fetched lazily by the detail panel for
 *  whichever drone is open (not part of the list fetch: fetching it for
 *  every row would be an N+1 call per drone the list endpoint already
 *  serves cheaply in one request). */
export async function fetchCustodianDroneConfig(serial: string): Promise<CustodianDroneConfig> {
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/config`,
    {
      headers: { Accept: 'application/json' },
    },
  );
  if (!res.ok) throw new Error(`Custodian API ${res.status}`);
  return (await res.json()) as CustodianDroneConfig;
}

/** `PUT /ownership` — per Postman, requires a `reason` string the UI has no
 *  field for yet. `reason` is a caller-supplied placeholder until product
 *  defines a real one; see `OWNERSHIP_REASON` in InvDetailPanel.tsx. */
export async function putOwnershipFlag(
  serial: string,
  enabled: boolean,
  reason: string,
): Promise<void> {
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/ownership`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ enabled, reason }),
    },
  );
  if (!res.ok) throw await custodianError(res, 'PUT /ownership');
}

/** `PUT /drones/{serial}/geofence-buffer` | `/obstacle-buffer` (ENG-294). The
 *  only way to CLEAR a buffer: `null` writes NULL ("not set"), whereas `PUT
 *  /config` reads null as "leave unchanged". The body is validated >= 0 (422
 *  otherwise) and an unknown serial is a 404; the response is
 *  `{ buffer_m, updated_at }` but only the status matters here. */
export async function putAircraftBuffer(
  serial: string,
  key: AircraftBufferKey,
  value: number | null,
): Promise<void> {
  const path = key === 'geofence_buffer_m' ? 'geofence-buffer' : 'obstacle-buffer';
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/${path}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ buffer_m: value }),
    },
  );
  if (!res.ok) throw await custodianError(res, `PUT /${path}`);
}

/** `PUT /adapter-config` — the dedicated way to change an aircraft's adapter
 *  type (ENG-227/228; the same field `PUT /config` also accepts). Only
 *  `adapter_type` is sent: `adapter_config` is replaced wholesale by this
 *  endpoint, so omitting it keeps the aircraft's adapter settings. The API
 *  accepts any text and its response shape isn't documented, so only the status
 *  is checked and nothing is read back — the caller records the value it sent. */
export async function putAdapterType(serial: string, adapterType: string): Promise<void> {
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/adapter-config`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ adapter_type: adapterType }),
    },
  );
  if (!res.ok) throw await custodianError(res, 'PUT /adapter-config');
}

/** Fields `PUT /config` persists, per the 2026-08-27 spec §1.2.
 *
 *  That update widened the writable set to cover every new column. The block
 *  previously listed here as "confirmed ignored" — `networkid_streaming_enabled`,
 *  `adsb_out_device`, `adsb_hex_mode`, `adsb_hex`, `gcs_plugin*`, `c2_links`,
 *  `video_feeds`, `host`, `port`, `protocol` — is now writable and appears
 *  below. Three exclusions remain:
 *
 *   - `daa_rules` — intentionally not settable here; use `addDaaRule` /
 *     `deleteDaaRule` (spec §1.5).
 *   - `ownership_flag_enabled` — has its own endpoint, which additionally
 *     takes the audit `reason` this one has no field for.
 *   - `custodian_*` / `adsb_observed` / `data_source_*` observations — BE
 *     populates those; only `data_source_max_latency_ms` is a setting.
 *
 *  IMPORTANT: the endpoint rejects an explicit `null` with HTTP 400. To
 *  clear a value you must omit the key, so every optional field below is
 *  omitted rather than nulled — see `putCustodianConfig`.
 *
 *  NOTE: `adapter_config` is free-form, but the server strips `host`/`port`/
 *  `protocol` from it before saving — those are dedicated columns now and
 *  belong at the top level (spec §1.2 note, §1.4). */
export interface CustodianConfigPatch {
  adapter_type?: string;
  adapter_config?: Record<string, unknown>;
  publish_rate_hz?: number;

  oi_automation_enabled?: boolean;
  oi_automation_square_m?: number;
  oi_automation_radius_m?: number;
  oi_automation_amend_min_trigger_m?: number;
  oi_automation_amend_max_alt_agl_m?: number;
  oi_automation_amend_enabled?: boolean;
  oi_automation_time_amend_enabled?: boolean;
  /** Whole seconds, 60-1800. */
  oi_automation_time_amend_trigger_s?: number;
  /** Whole seconds, 60-3600. */
  oi_automation_time_amend_extension_s?: number;
  horizontal_speed_ms?: number;
  vertical_speed_ms?: number;
  fv_buffer_m?: number;
  cv_buffer_m?: number;
  waypoint_buffer_m?: number;
  /** `PUT /config` treats null as "leave unchanged", so a value can only be set
   *  here — clearing goes through `putAircraftBuffer(…, null)`. */
  geofence_buffer_m?: number;
  obstacle_buffer_m?: number;

  tisb_rebroadcast_enabled?: boolean;
  adsb_surrogate_enabled?: boolean;
  networkid_streaming_enabled?: boolean;
  adsb_in_enabled?: boolean;
  adsb_out_enabled?: boolean;
  adsb_out_device?: AdsbOutDevice;
  adsb_hex_mode?: AdsbHexMode;
  adsb_hex?: string;
  /** `""` clears it; the API trims and upper-cases, and 422s anything but 1-8 of A-Z / 0-9. */
  adsb_identification_code?: string;

  default_pilot_name?: string;
  default_pilot_fs_guid?: string;
  nmac_crewed_h?: number;
  nmac_crewed_v?: number;
  nmac_uncrewed_h?: number;
  nmac_uncrewed_v?: number;
  swc_crewed_h?: number;
  swc_crewed_v?: number;
  swc_uncrewed_h?: number;
  swc_uncrewed_v?: number;
  eval_distance_m?: number;
  data_source_max_latency_ms?: number;

  gcs_plugin?: string;
  gcs_plugin_status?: string;
  c2_links?: number;
  video_feeds?: number;
  host?: string;
  port?: number;
  protocol?: string;
}

/** The enumerated values `adsb_out_device` accepts (spec §1.2). Sent and
 *  returned exactly as spelled here — note the capitalisation and the space
 *  in "SkyEcho 2". */
export const ADSB_OUT_DEVICES = ['Ping978ec', 'Ping2020i', 'TISB', 'SkyEcho 2', 'other'] as const;
export type AdsbOutDevice = (typeof ADSB_OUT_DEVICES)[number];

/** `adsb_hex_mode` on the wire is capitalised (spec §1.2). The store keeps it
 *  lowercase — see `normaliseHexMode`. */
export const ADSB_HEX_MODES = ['Static', 'Dynamic'] as const;
export type AdsbHexMode = (typeof ADSB_HEX_MODES)[number];

/** `PUT /config` — the single write path for drone configuration, replacing
 *  the per-concern `/oi-automation`, `/oi-defaults`, `/adsb-surrogate` and
 *  `/tisb-rebroadcast` calls (BE's direction, 2026-08-21). Returns the full
 *  updated config.
 *
 *  Strips `undefined` AND `null` before sending: a null body value is a 400,
 *  so an unset field must simply not appear in the JSON. */
export async function putCustodianConfig(
  serial: string,
  patch: CustodianConfigPatch,
): Promise<CustodianDroneConfig> {
  const body = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v !== undefined && v !== null),
  );
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/config`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await custodianError(res, 'PUT /config');
  return (await res.json()) as CustodianDroneConfig;
}

/** A failed Custodian write. `detail` is the API's own message when it sent one
 *  — a 422 explains itself, so it is shown as is rather than as a bare status.
 *  From api-custodian 1.0.24 every error body is `{error}` (ENG-373), naming the
 *  field and the limit. Older versions answered a bad value with FastAPI's
 *  `{detail:[{msg}]}` and a wrong URL with `{detail}`, so those are still read. */
export class CustodianApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string | null,
    where: string,
  ) {
    super(`Custodian API ${status} on ${where}`);
  }
}

async function custodianError(res: Response, where: string): Promise<CustodianApiError> {
  let detail: string | null = null;
  try {
    const body = (await res.json()) as { error?: unknown; detail?: unknown; message?: unknown };
    if (typeof body.error === 'string') detail = body.error;
    else if (typeof body.detail === 'string') detail = body.detail;
    else if (typeof body.message === 'string') detail = body.message;
    else if (Array.isArray(body.detail)) {
      const msgs = body.detail
        .map((d) => (d as { msg?: unknown } | undefined)?.msg)
        .filter((m): m is string => typeof m === 'string')
        .map((m) => m.replace(/^Value error, /, ''));
      if (msgs.length) detail = msgs.join('; ');
    }
  } catch {
    // Not JSON — the status alone is all there is.
  }
  return new CustodianApiError(res.status, detail, where);
}

/** Both DAA endpoints return the FULL updated array rather than the single
 *  mutated rule, so callers replace their copy wholesale instead of splicing
 *  — the server is authoritative about ordering and the generated `id`. */
interface DaaRulesResponse {
  daa_rules: DaaRuleRecord[] | null;
}

/** `POST /daa/add_rule` (spec §1.5) — appends one free-form rule object to
 *  `drones.drones.daa_rules`.
 *
 *  Any `id` on `rule` is discarded server-side and replaced with a generated
 *  one, so we strip it before sending rather than implying the client gets a
 *  say. Returns the full updated array. */
export async function addDaaRule(serial: string, rule: DaaRuleRecord): Promise<DaaRuleRecord[]> {
  const { id: _ignored, ...body } = rule;
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/daa/add_rule`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await custodianError(res, 'POST /daa/add_rule');
  return ((await res.json()) as DaaRulesResponse).daa_rules ?? [];
}

/** `DELETE /daa/rules/{rule_id}` (spec §1.5) — removes the rule whose `id`
 *  matches. Returns the full updated array. */
export async function deleteDaaRule(serial: string, ruleId: string): Promise<DaaRuleRecord[]> {
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/daa/rules/${encodeURIComponent(ruleId)}`,
    { method: 'DELETE', headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw await custodianError(res, 'DELETE /daa/rules');
  return ((await res.json()) as DaaRulesResponse).daa_rules ?? [];
}

/** `GET /health` — the only source for GCS-plugin session/version. Everything
 *  it returns about connectivity is also on the list record, but the two
 *  plugin fields are not, so the detail panel fetches it per open drone. */
export interface CustodianHealth {
  serial_number: string;
  adapter_connected: boolean;
  last_telemetry_at: string | null;
  plugin_session_id: string | null;
  plugin_version: string | null;
  retired_at: string | null;
}

export async function fetchCustodianHealth(serial: string): Promise<CustodianHealth> {
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/health`,
    { headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error(`Custodian API ${res.status}`);
  return (await res.json()) as CustodianHealth;
}

/**
 * `GET /{serial}/mission` — the drone's **currently active** mission binding
 * (`flight_status = 1`) from `flights.binding`. Read-only.
 *
 * Verified live 2026-08-24 across all 27 stage aircraft: **3 return 200, 24
 * return 404**. A 404 is the ordinary "this drone has no active binding" case,
 * NOT an error — hence `CustodianMission | null` rather than a throw. Only a
 * genuine failure (5xx, network) throws.
 *
 * Population is uneven. The one aircraft flying at probe time carried the full
 * record (`mission_name`, `mission_type`, `mission_location`, `pilot_full_name`,
 * `pilot_id`, `mission_guid`, `callsign`); the two older bindings had those as
 * null with only `mission_name` + `oi_window` set. Every field is therefore
 * nullable and the UI renders '—' per field rather than hiding the section.
 *
 * NOTE for Workstream B: this carries **no scheduled window** — `created_at` and
 * `last_message_at` are record timestamps, not a flight's planned start/end. It
 * cannot answer "what is scheduled next" without a backend addition, and it is
 * one call per serial, so it can't cheaply drive a fleet-wide list either.
 */
export interface CustodianMission {
  mission_guid: string | null;
  callsign: string | null;
  pilot_id: string | null;
  pilot_full_name: string | null;
  mission_name: string | null;
  mission_type: string | null;
  mission_location: string | null;
  drone_model: string | null;
  drone_type: string | null;
  /** 1 = active binding. The endpoint only ever returns active bindings, so
   *  this is 1 on every record observed — kept because the backend models it. */
  flight_status: number | null;
  created_at: string | null;
  last_message_at: string | null;
  oi_window: {
    /** Ring array `[[[lng, lat], …]]` — same convention as `flight_volume`. */
    operational_volume?: unknown;
    utm_declaration_id?: string | null;
    /** Observed: `"active"`, `"ended"`. */
    utm_state?: string | null;
  } | null;
}

export async function fetchCustodianMission(serial: string): Promise<CustodianMission | null> {
  const res = await fetch(
    `${config.custodian.base}/api/v1/drones/${encodeURIComponent(serial)}/mission`,
    { headers: { Accept: 'application/json' } },
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Custodian API ${res.status}`);
  return (await res.json()) as CustodianMission;
}

/** Count the vertices in the binding's operational volume, for the "polygon
 *  present" readout. Returns null when the shape isn't the expected ring array
 *  rather than guessing a count. */
export function missionVolumeVertexCount(ov: unknown): number | null {
  if (!Array.isArray(ov) || ov.length === 0) return null;
  const ring = ov[0];
  return Array.isArray(ring) ? ring.length : null;
}

/**
 * `GET /pilots/active` — the tenant's active pilots, for the Default Pilot
 * picker on the drone detail panel (ClickUp 86e30bd6t, BE comment 2026-09-01).
 *
 * Unlike drones there is NO local mirror: the endpoint reads through to
 * DroneLogbook's `/user` API on every call, pages it until `has_more` is
 * falsy, and returns only users that are `ACTIVE` **and** carry the "Pilot"
 * role. Two consequences for callers:
 *
 *  - it inherits DLB's availability — `503` when the custodian has no
 *    `DLB_API_KEY`/`DLB_COMPANY_URL` configured, `502` when the DLB request
 *    itself fails. Both are ordinary operational states, not bugs here.
 *  - it needs custodian image **>= 1.0.7**. DLB's `data` array contains null
 *    entries in production and older images 500 on them.
 *
 * Read-only by design: pilots stay owned and edited in DLB, so there is no
 * POST/PUT counterpart. The only write is carrying the chosen pilot onto the
 * drone via `default_pilot_fs_guid` / `default_pilot_name` on `PUT /config`.
 *
 * Shape verified against the production sample attached to the ticket
 * (2026-09-01, 13 pilots across two `company_guid`s).
 */
export interface CustodianPilot {
  /** DLB user guid. Goes to `default_pilot_fs_guid` on `PUT /config` — the
   *  field is named "fs_guid" but BE stores the DLB guid in it. */
  pilot_guid: string | null;
  full_name: string | null;
  /** DLB login, i.e. the email address. */
  login: string | null;
  company_guid: string | null;
  country: string | null;
  license_number: string | null;
  license_type: string | null;
  phone: string | null;
  roles: string[];
  /** Always "ACTIVE" — the endpoint filters on it server-side. */
  status: string;
}

interface PilotsResponse {
  pilots: CustodianPilot[];
  total: number;
}

/**
 * `CustodianPilot` → `InventoryUser`, so the Inventory Users tab has a live
 * source (ClickUp 86e30bd6t). Before this it ran on the MSW mock only and was
 * empty in live mode — the Custodian had no user endpoint at all.
 *
 * Nine of the seventeen fields map straight across. The rest are simply NOT in
 * DLB's user payload and are left null for the UI to render as '—'; none of
 * them is inferred:
 *
 *  - `is_admin` stays FALSE for everyone. DLB's "Manager" role is the closest
 *    thing, but "Manager" is not the same claim as "admin" and guessing would
 *    put an Admin badge on people who may not be one. The Admin filter chip
 *    therefore matches nobody in live mode, which is the truthful answer to a
 *    question this endpoint cannot answer.
 *  - `timezone`, `language`, `last_login`, `date_created` — absent upstream.
 *  - `legal_profile`, `notes` — absent upstream (already nullable).
 *
 * Scope caveat: `/pilots/active` returns only users that are ACTIVE **and**
 * hold the Pilot role, so this is a pilot roster, not the full account list.
 * Non-pilots and deactivated accounts do not appear, and the Inactive filter
 * matches nothing.
 */
export function toInventoryUser(p: CustodianPilot): InventoryUser {
  const guid = p.pilot_guid ?? '';
  return {
    user_id: guid,
    guid,
    full_name: p.full_name ?? '',
    email: p.login ?? '',
    phone: strOrNull(p.phone),
    status: 'ACTIVE',
    roles: p.roles ?? [],
    is_admin: false,
    country: p.country ?? '',
    timezone: null,
    language: null,
    last_login: null,
    date_created: null,
    legal_profile: null,
    license_type: strOrNull(p.license_type),
    license_number: strOrNull(p.license_number),
    notes: null,
  };
}

export async function fetchActivePilots(): Promise<CustodianPilot[]> {
  const res = await fetch(`${config.custodian.base}/api/v1/pilots/active`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Custodian API ${res.status} on GET /pilots/active`);
  const json = (await res.json()) as PilotsResponse;
  // Both key fields are nullable in the contract. A pilot without a guid can't
  // be persisted and one without a name can't be labelled, so neither belongs
  // in a picker — drop them here rather than rendering a blank option that
  // writes an empty guid. Non-object entries are the DLB null-row case the
  // 1.0.7 fix covers server-side; guarded anyway so an older image degrades to
  // a short list instead of a crashed panel.
  return (json.pilots ?? []).filter((p): p is CustodianPilot =>
    Boolean(p && typeof p === 'object' && p.pilot_guid && p.full_name),
  );
}

/** Overlays `/health` — refreshes the GCS Plugin / Plugin Status fields when
 *  it has a live session to report, and refreshes connectivity from the
 *  endpoint that tracks it directly (the list record's `adapter_connected`
 *  can lag).
 *
 *  `/health` only ever reports a plugin when one is actively connected right
 *  now — it has no "last known" value. When it has nothing (both
 *  `plugin_version` and `plugin_session_id` null, the common case), that
 *  means "no live session", not "no plugin" — the list/`/config` value
 *  already on `d` is still the best answer, so it's kept rather than
 *  overwritten with 'none'. */
export function applyHealthToInventoryDrone(d: InventoryDrone, h: CustodianHealth): InventoryDrone {
  const plugin = strOrNull(h.plugin_version)
    ? `GCS Plugin v${h.plugin_version}`
    : h.plugin_session_id
      ? 'GCS Plugin (session active)'
      : null;
  const healthAgeMs = h.last_telemetry_at
    ? Date.now() - new Date(h.last_telemetry_at).getTime()
    : Number.POSITIVE_INFINITY;
  return {
    ...d,
    data_source: d.data_source
      ? {
          ...d.data_source,
          status: dataSourceStatus(h.adapter_connected, healthAgeMs),
          last_seen_at: h.last_telemetry_at ?? d.data_source.last_seen_at,
        }
      : d.data_source,
    gcs_plugin: plugin ?? d.gcs_plugin,
    gcs_plugin_status: plugin ? (h.adapter_connected ? 'online' : 'offline') : d.gcs_plugin_status,
    custodian_status: h.retired_at ? 'none' : h.adapter_connected ? 'online' : 'offline',
    dtwin_sync_label: agoLabel(h.last_telemetry_at ?? d.dtwin_state.last_telemetry_at),
    dtwin_state: {
      ...d.dtwin_state,
      adapter_connected: h.adapter_connected,
      last_telemetry_at: h.last_telemetry_at ?? d.dtwin_state.last_telemetry_at,
    },
  };
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** DLB-sourced numerics come across as strings (`"20"`, `"0"`, `""`).
 *  Returns null for empty/unparseable so the UI renders '—' rather than
 *  a misleading 0. */
function numStr(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string' || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** DLB writes `"0"` into unfilled airframe-spec fields. A zero weight or
 *  zero max speed is physically impossible, so it means "not recorded" and
 *  maps to null ('—') rather than a confident-looking 0. Payload capacity is
 *  deliberately NOT routed through this — zero payload is a real value. */
function specNum(v: unknown): number | null {
  const n = numStr(v);
  return n != null && n > 0 ? n : null;
}

/** DLB `max_flight_time` arrives in SECONDS on the live API (`"21600"` on a
 *  Mavic 3T = 360 min), while the UI field is minutes. Confirmed against
 *  every stage aircraft — no record uses minutes. Zero means "not recorded"
 *  in DLB, so it maps to null rather than a literal 0 min. */
function flightTimeMinutes(v: unknown): number | null {
  const sec = numStr(v);
  if (sec == null || sec <= 0) return null;
  return Math.round(sec / 60);
}

/** DLB status strings → our union. `retired_at` wins over any status text. */
function mapStatus(
  raw: string | null | undefined,
  retiredAt: string | null,
): InventoryDrone['status'] {
  if (retiredAt) return 'Decommissioned';
  const s = (raw ?? '').trim().toLowerCase();
  if (s.includes('maintenance')) return 'Maintenance';
  if (s.includes('grounded')) return 'Grounded';
  if (s.includes('decommission') || s.includes('retired')) return 'Decommissioned';
  return 'Airworthy';
}

/** Overlays the flat `/config` record onto a drone already built by
 *  `toInventoryDrone()`. `/config` is the only source for
 *  host/port/protocol, c2_links, video_feeds and surveillance mode, and it
 *  re-serves the aircraft fields — so it fills anything the list left empty
 *  without ever overwriting a value the list already supplied.
 *
 *  The three toggle fields are ALSO on the list endpoint (hence already on
 *  `d`), but overlaying them from `cfg` makes `/config`'s cache entry —
 *  which `useSaveCustodianConfig`/`useSetOwnershipFlag` both write into
 *  directly — the value the detail panel's toggles actually read. Confirmed
 *  bug otherwise: save a toggle, switch to another drone and back without a
 *  full reload, and it showed the pre-save value because these fields were
 *  only ever read from the separately-cached list query. */
export function applyConfigToInventoryDrone(
  d: InventoryDrone,
  cfg: CustodianDroneConfig,
): InventoryDrone {
  const merged: InventoryDrone = {
    ...d,
    // Fields only `/config` carries.
    adapter_host: strOrNull(cfg.host) ?? d.adapter_host,
    adapter_port: num(cfg.port) ?? d.adapter_port,
    adapter_protocol: strOrNull(cfg.protocol) ?? d.adapter_protocol,
    c2_links: num(cfg.c2_links) ?? d.c2_links,
    video_feeds: num(cfg.video_feeds) ?? d.video_feeds,
    // `surveillance` was dropped from /config in the 2026-08-21 widening and
    // has no other source — left as-is so it renders '—'.
    // Aircraft fields — only fill gaps the list didn't already cover.
    registration: strOrNull(cfg.tc_reg) ?? d.registration,
    ec_tc_reg: strOrNull(cfg.tc_reg) ?? d.ec_tc_reg,
    ec_callsign: strOrNull(cfg.callsign) ?? d.ec_callsign,
    brand: d.brand || str(cfg.brand),
    model: d.model || str(cfg.model),
    drone_type: d.drone_type || str(cfg.type),
    firmware_version: d.firmware_version || str(cfg.firmware),
    hardware_version: d.hardware_version || str(cfg.hardware),
    weight_kg: d.weight_kg ?? specNum(cfg.weight),
    payload_capacity_kg: d.payload_capacity_kg ?? numStr(cfg.payload_capacity),
    max_horizontal_speed: d.max_horizontal_speed ?? specNum(cfg.max_horizontal_speed),
    max_vertical_speed: d.max_vertical_speed ?? specNum(cfg.max_vertical_speed),
    max_flight_time_min: d.max_flight_time_min ?? flightTimeMinutes(cfg.max_flight_time),
    insurable_value: d.insurable_value || String(cfg.insurable_value ?? ''),
    // A retired aircraft stays Decommissioned — `/config` has no retired_at
    // to re-derive that from, so the list-derived status wins in that case.
    status: d.status === 'Decommissioned' ? d.status : mapStatus(cfg.status, null),
    // ── Fields the 2026-08-21 widening added. Every one is null on every
    //    aircraft so far, so each falls back to what the drone already had
    //    rather than blanking a populated mock value. ──
    default_pilot_name: strOrNull(cfg.default_pilot_name) ?? d.default_pilot_name,
    default_pilot_fs_guid: strOrNull(cfg.default_pilot_fs_guid) ?? d.default_pilot_fs_guid,
    // ── Separation, split by BE on 2026-08-27 ──
    // The eight columns are now the source of truth. `nmac_distance_m` /
    // `swc_distance_m` survive only as a derived summary for the two rows
    // that still show a single figure: the crewed HORIZONTAL value, which is
    // what the old scalar always meant. A pre-split tenant still sends the
    // scalars, so those are the fallback rather than the primary.
    nmac_h_crewed_m: num(cfg.nmac_crewed_h) ?? d.nmac_h_crewed_m,
    nmac_v_crewed_m: num(cfg.nmac_crewed_v) ?? d.nmac_v_crewed_m,
    nmac_h_uncrewed_m: num(cfg.nmac_uncrewed_h) ?? d.nmac_h_uncrewed_m,
    nmac_v_uncrewed_m: num(cfg.nmac_uncrewed_v) ?? d.nmac_v_uncrewed_m,
    swc_h_crewed_m: num(cfg.swc_crewed_h) ?? d.swc_h_crewed_m,
    swc_v_crewed_m: num(cfg.swc_crewed_v) ?? d.swc_v_crewed_m,
    swc_h_uncrewed_m: num(cfg.swc_uncrewed_h) ?? d.swc_h_uncrewed_m,
    swc_v_uncrewed_m: num(cfg.swc_uncrewed_v) ?? d.swc_v_uncrewed_m,
    nmac_distance_m: num(cfg.nmac_crewed_h) ?? num(cfg.nmac_distance_m) ?? d.nmac_distance_m,
    swc_distance_m: num(cfg.swc_crewed_h) ?? num(cfg.swc_distance_m) ?? d.swc_distance_m,
    eval_distance_m: num(cfg.eval_distance_m) ?? d.eval_distance_m,
    data_source_max_latency_ms: num(cfg.data_source_max_latency_ms) ?? d.data_source_max_latency_ms,
    // BE-reported monitoring wins over the locally-derived reading whenever
    // it has actually populated a status.
    data_source: dataSourceFromConfig(cfg) ?? d.data_source,
    adsb_observed: cfg.adsb_observed
      ? {
          broadcasting: cfg.adsb_observed.broadcasting ?? false,
          last_icao_hex: strOrNull(cfg.adsb_observed.last_icao_hex),
          last_seen_by_cifib: strOrNull(cfg.adsb_observed.last_seen_by_cifib),
        }
      : d.adsb_observed,
    daa_rules: cfg.daa_rules?.length ? cfg.daa_rules.map(toDaaRule) : d.daa_rules,
    adsb_out_device: strOrNull(cfg.adsb_out_device) ?? d.adsb_out_device,
    ec_adsb_hex: strOrNull(cfg.adsb_hex) ?? d.ec_adsb_hex,
    // `null` means "not set" (or just cleared), so only an ABSENT key falls back.
    adsb_identification_code:
      cfg.adsb_identification_code === undefined
        ? d.adsb_identification_code
        : strOrNull(cfg.adsb_identification_code),
    adsb_hex_mode: normaliseHexMode(cfg.adsb_hex_mode) ?? d.adsb_hex_mode,
    gcs_plugin: strOrNull(cfg.gcs_plugin) ?? d.gcs_plugin,
    gcs_plugin_status: normalisePluginStatus(cfg.gcs_plugin_status) ?? d.gcs_plugin_status,
    custodian_runtime: strOrNull(cfg.custodian_runtime) ?? d.custodian_runtime,
    custodian_configured: cfg.custodian_configured ?? d.custodian_configured,
    agent_deployed: cfg.custodian_deployed ?? d.agent_deployed,
    // Read-only on this endpoint (a PUT carrying it is silently ignored), but
    // still the freshest value BE has.
    networkid_streaming_enabled: cfg.networkid_streaming_enabled ?? d.networkid_streaming_enabled,
    tisb_rebroadcast_enabled: cfg.tisb_rebroadcast_enabled,
    publish_rate_hz: cfg.publish_rate_hz,
    oi_automation_square_m: num(cfg.oi_automation_square_m) ?? d.oi_automation_square_m,
    oi_automation_radius_m: num(cfg.oi_automation_radius_m) ?? d.oi_automation_radius_m,
    oi_automation_amend_min_trigger_m:
      num(cfg.oi_automation_amend_min_trigger_m) ?? d.oi_automation_amend_min_trigger_m,
    oi_automation_amend_max_alt_agl_m:
      num(cfg.oi_automation_amend_max_alt_agl_m) ?? d.oi_automation_amend_max_alt_agl_m,
    oi_automation_amend_enabled: cfg.oi_automation_amend_enabled ?? d.oi_automation_amend_enabled,
    oi_automation_time_amend_enabled:
      cfg.oi_automation_time_amend_enabled ?? d.oi_automation_time_amend_enabled,
    oi_automation_time_amend_trigger_s:
      num(cfg.oi_automation_time_amend_trigger_s) ?? d.oi_automation_time_amend_trigger_s,
    oi_automation_time_amend_extension_s:
      num(cfg.oi_automation_time_amend_extension_s) ?? d.oi_automation_time_amend_extension_s,
    waypoint_buffer_m: num(cfg.waypoint_buffer_m) ?? d.waypoint_buffer_m,
    // `null` here means "cleared", so unlike the fields around it this must NOT
    // fall back to the list value — that would put a cleared 30 m back on screen.
    ...bufferFields(cfg),
    // Toggle state — see the block comment above for why these are re-read.
    ownership_flag_enabled: cfg.ownership_flag_enabled,
    oi_automation_enabled: cfg.oi_automation_enabled,
    adsb_surrogate_enabled: cfg.adsb_surrogate_enabled,
    adsb_out: cfg.adsb_out_enabled ? 'Enabled' : 'Disabled',
    adsb_in: cfg.adsb_in_enabled ? 'Active' : 'N/A',
    horizontal_speed_ms: cfg.horizontal_speed_ms,
    vertical_speed_ms: cfg.vertical_speed_ms,
    fv_lateral_buffer_m: num(cfg.fv_buffer_m) ?? d.fv_lateral_buffer_m,
    cv_lateral_buffer_m: num(cfg.cv_buffer_m) ?? d.cv_lateral_buffer_m,
    yaml_created: true,
  };
  return { ...merged, yaml: buildCustodianYaml(merged, cfg) };
}

/** Live equivalent of the mockup's `custodian_config.yaml` block, built only
 *  from fields `/config` actually returned — never fabricates a value.
 *  Anything the API sends as null/'' is omitted rather than rendered blank. */
function buildCustodianYaml(d: InventoryDrone, cfg: CustodianDroneConfig): string {
  const lines: string[] = [
    `# Custodian API Config · ${d.display_name}${cfg.model ? ` · ${cfg.model}` : ''}`,
    `# Source: GET /drones/${cfg.serial_number}/config`,
    'custodian:',
    `  serial_number: ${cfg.serial_number}`,
    `  dlb_guid: ${cfg.dlb_guid}`,
  ];
  if (cfg.callsign) lines.push(`  callsign: ${cfg.callsign}`);
  if (cfg.tc_reg) lines.push(`  tc_reg: ${cfg.tc_reg}`);

  lines.push(
    'adapter:',
    `  type: ${cfg.adapter_type}`,
    `  publish_rate_hz: ${cfg.publish_rate_hz}`,
  );
  if (cfg.host) lines.push(`  host: ${cfg.host}`);
  if (cfg.port != null) lines.push(`  port: ${cfg.port}`);
  if (cfg.protocol) lines.push(`  protocol: ${cfg.protocol}`);

  lines.push(
    'adsb:',
    `  out_enabled: ${cfg.adsb_out_enabled}`,
    `  in_enabled: ${cfg.adsb_in_enabled}`,
    `  surrogate_enabled: ${cfg.adsb_surrogate_enabled}`,
  );
  // Real values as of 2026-08-27 — hardcoded null before that, hence omitted.
  if (cfg.adsb_out_device) lines.push(`  out_device: ${cfg.adsb_out_device}`);
  if (cfg.adsb_hex_mode) lines.push(`  hex_mode: ${cfg.adsb_hex_mode}`);
  if (cfg.adsb_hex) lines.push(`  hex: ${cfg.adsb_hex}`);
  if (cfg.adsb_identification_code)
    lines.push(`  identification_code: ${cfg.adsb_identification_code}`);

  lines.push(
    'oi_defaults:',
    `  horizontal_speed_ms: ${cfg.horizontal_speed_ms}`,
    `  vertical_speed_ms: ${cfg.vertical_speed_ms}`,
  );
  if (cfg.fv_buffer_m != null) lines.push(`  fv_buffer_m: ${cfg.fv_buffer_m}`);
  if (cfg.cv_buffer_m != null) lines.push(`  cv_buffer_m: ${cfg.cv_buffer_m}`);
  if (d.waypoint_buffer_m != null) lines.push(`  waypoint_buffer_m: ${d.waypoint_buffer_m}`);
  if (d.oi_automation_square_m != null)
    lines.push(`  oi_automation_square_m: ${d.oi_automation_square_m}`);
  if (d.oi_automation_radius_m != null)
    lines.push(`  oi_automation_radius_m: ${d.oi_automation_radius_m}`);
  if (d.oi_automation_amend_min_trigger_m != null)
    lines.push(`  oi_automation_amend_min_trigger_m: ${d.oi_automation_amend_min_trigger_m}`);
  if (d.oi_automation_amend_max_alt_agl_m != null)
    lines.push(`  oi_automation_amend_max_alt_agl_m: ${d.oi_automation_amend_max_alt_agl_m}`);
  if (cfg.oi_automation_time_amend_enabled != null)
    lines.push(`  oi_automation_time_amend_enabled: ${cfg.oi_automation_time_amend_enabled}`);
  if (d.oi_automation_time_amend_trigger_s != null)
    lines.push(`  oi_automation_time_amend_trigger_s: ${d.oi_automation_time_amend_trigger_s}`);
  if (d.oi_automation_time_amend_extension_s != null)
    lines.push(`  oi_automation_time_amend_extension_s: ${d.oi_automation_time_amend_extension_s}`);

  // `separation_defaults` mirrors the nested { h, v } sub-maps the YAML write
  // path accepts (spec §1.3). Each pair is emitted only when BE has a value —
  // a half-populated pair would read as a deliberate 0.
  const separation: Array<[string, number | null, number | null]> = [
    ['nmac_crewed_m', cfg.nmac_crewed_h, cfg.nmac_crewed_v],
    ['nmac_uncrewed_m', cfg.nmac_uncrewed_h, cfg.nmac_uncrewed_v],
    ['swc_crewed_m', cfg.swc_crewed_h, cfg.swc_crewed_v],
    ['swc_uncrewed_m', cfg.swc_uncrewed_h, cfg.swc_uncrewed_v],
  ];
  const separationSet = separation.filter(([, h, v]) => h != null || v != null);
  if (separationSet.length > 0) {
    lines.push('separation_defaults:');
    for (const [key, h, v] of separationSet) {
      lines.push(`  ${key}:`);
      if (h != null) lines.push(`    h: ${h}`);
      if (v != null) lines.push(`    v: ${v}`);
    }
    if (cfg.eval_distance_m != null) lines.push(`  eval_distance_m: ${cfg.eval_distance_m}`);
  }

  if (cfg.c2_links != null || cfg.video_feeds != null) {
    lines.push('c2:');
    if (cfg.c2_links != null) lines.push(`  links: ${cfg.c2_links}`);
    if (cfg.video_feeds != null) lines.push(`  video_feeds: ${cfg.video_feeds}`);
  }

  if (cfg.gcs_plugin || cfg.gcs_plugin_status) {
    lines.push('gcs:');
    if (cfg.gcs_plugin) lines.push(`  plugin: ${cfg.gcs_plugin}`);
    if (cfg.gcs_plugin_status) lines.push(`  plugin_status: ${cfg.gcs_plugin_status}`);
  }

  lines.push(
    `oi_automation_enabled: ${cfg.oi_automation_enabled}`,
    `ownership_flag_enabled: ${cfg.ownership_flag_enabled}`,
  );
  if (cfg.networkid_streaming_enabled != null) {
    lines.push(`networkid_streaming_enabled: ${cfg.networkid_streaming_enabled}`);
  }
  return `${lines.join('\n')}\n`;
}

/** BE-reported data-source monitoring, when it has actually populated one.
 *  Returns null while `data_source_status` is null (true for every aircraft
 *  so far) so the caller can fall back to the derived reading. */
function dataSourceFromConfig(cfg: CustodianDroneConfig): DataSourceMonitor | null {
  const raw = (cfg.data_source_status ?? '').toUpperCase();
  if (raw !== 'HEALTHY' && raw !== 'DEGRADED' && raw !== 'STALE') return null;
  return {
    mechanism: str(cfg.data_source_mechanism),
    rate_hz: num(cfg.data_source_rate_hz),
    latency_ms_p50: num(cfg.data_source_latency_ms_p50),
    latency_ms_p95: num(cfg.data_source_latency_ms_p95),
    status: raw,
    last_seen_at: strOrNull(cfg.data_source_last_seen_at),
  };
}

/** `adsb_hex_mode` arrives capitalised (`"Static"` / `"Dynamic"`) — the store
 *  keeps it lowercase. The pre-2026-08-27 comparison was a literal `=== 'static'`,
 *  which never matched the wire value and had no `Dynamic` branch at all; it
 *  went unnoticed only because BE hardcoded the field to null. Returns null for
 *  anything unrecognised so the caller keeps whatever it already had. */
function normaliseHexMode(raw: string | null | undefined): InventoryDrone['adsb_hex_mode'] | null {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'static') return 'static';
  if (s === 'dynamic') return 'dynamic';
  return null;
}

/** Same treatment for `gcs_plugin_status`, whose casing BE has never pinned
 *  down. Null when unrecognised — 'none' is a real state meaning "no plugin",
 *  which is not the same as "BE sent something we don't understand". */
function normalisePluginStatus(
  raw: string | null | undefined,
): InventoryDrone['gcs_plugin_status'] | null {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'online') return 'online';
  if (s === 'offline') return 'offline';
  if (s === 'none') return 'none';
  return null;
}

const DAA_PRIORITY: Record<string, DaaRule['priority']> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
};

/** Project a free-form rule object onto the card shape the UI renders.
 *
 *  Per spec §1.5 only `id` is guaranteed — `condition` / `action` / `priority`
 *  / `enabled` are conventions, not a contract. Anything missing renders as
 *  an empty string and the card falls back to showing the raw object, so a
 *  rule written by some other producer is still visible rather than blank.
 *  `raw` carries the untouched record through for that fallback and for the
 *  delete call, which needs the server's own `id`. */
function toDaaRule(r: DaaRuleRecord, i: number): DaaRule {
  return {
    rule_id: str(r.id) || `R${i + 1}`,
    priority: DAA_PRIORITY[str(r.priority).toLowerCase()] ?? 'Medium',
    condition: str(r.condition),
    result: str(r.action),
    enabled: typeof r.enabled === 'boolean' ? r.enabled : true,
    raw: r,
  };
}

function agoLabel(iso: string | null): string {
  if (!iso) return 'never';
  const sec = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

/** Inventory's adapter label, taken from the Custodian `adapter_type`. Known
 *  adapters get a friendly name (`mavlink`, `MAVLink`, `dji_fh2`, `DJI`,
 *  `adsb_out`, `ADS-B Out` — see `normaliseAdapter`); anything else is shown as
 *  written so a new backend adapter is never mislabelled; no value at all is
 *  '—'. Never defaults to MAVLink — that made every unmapped aircraft look like
 *  one. */
export function personalityLabel(adapterType: string | null | undefined): string {
  const raw = adapterType?.trim() ?? '';
  if (!raw) return '—';
  return normaliseAdapter(raw) ?? raw;
}

const MECHANISM: Record<string, string> = {
  mavlink: 'MAVLink telemetry stream',
  dji_fh2: 'DJI FlightHub 2 OSD',
  dji: 'DJI Cloud API OSD',
  adsb_out: 'ADS-B Out broadcast',
};

/** Data-source description for an adapter. Matches case-insensitively, so an
 *  operator-written `MAVLink` finds the `mavlink` entry; unknown types show as
 *  written. */
function mechanismFor(adapterType: string | null | undefined): string {
  // A record without an adapter_type must not break the whole mapper (the type
  // says string, the wire has been seen to disagree).
  if (adapterType == null) return '';
  const key = adapterType.trim().toLowerCase();
  if (Object.hasOwn(MECHANISM, key)) return MECHANISM[key]!;
  const kind = normaliseAdapter(adapterType);
  if (kind === 'MAVLink') return MECHANISM.mavlink!;
  if (kind === 'ADS-B Out') return MECHANISM.adsb_out!;
  return adapterType;
}

/** Data-source health, DERIVED from what the Custodian actually reports —
 *  there's no monitoring endpoint yet. Only `rate_hz` (the configured
 *  publish rate) and `last_seen_at` are real numbers; the p50/p95 latencies
 *  stay null and render '—' rather than being invented.
 *
 *  Returns null when the Custodian has never seen this aircraft at all —
 *  the panel then shows "Not monitored" instead of a fake STALE reading. */
function dataSourceStatus(connected: boolean, ageMs: number): DataSourceMonitor['status'] {
  if (connected && ageMs < 30_000) return 'HEALTHY';
  if (ageMs < 5 * 60_000) return 'DEGRADED';
  return 'STALE';
}

function deriveDataSource(c: CustodianDrone): DataSourceMonitor | null {
  if (!c.adapter_connected && !c.last_telemetry_at) return null;
  const ageMs = c.last_telemetry_at
    ? Date.now() - new Date(c.last_telemetry_at).getTime()
    : Number.POSITIVE_INFINITY;
  const status: DataSourceMonitor['status'] = dataSourceStatus(c.adapter_connected, ageMs);
  return {
    mechanism: mechanismFor(c.adapter_type),
    rate_hz: num(c.publish_rate_hz),
    latency_ms_p50: null,
    latency_ms_p95: null,
    status,
    last_seen_at: c.last_telemetry_at,
  };
}

/** ENG-265 — only DJI aircraft (FH2 / DJI Cloud API) get the FlightHub2 /
 *  Telemetry / Dock lights; everything else returns null. */
function djiStatusFrom(c: CustodianDrone): DjiStatus | null {
  const ls = c.live_status;
  const raw = ls?.link?.mechanism ?? c.data_source_mechanism;
  // The BE only sets the mechanism once an aircraft has sent data, so a DJI
  // adapter with no mechanism yet is still a DJI aircraft.
  const adapter = (c.adapter_type ?? '').toLowerCase();
  const mechanism: DjiStatus['mechanism'] | null =
    raw === 'fh2' || raw === 'dji_cloud_api'
      ? raw
      : adapter === 'dji_fh2'
        ? 'fh2'
        : adapter.startsWith('dji')
          ? 'dji'
          : null;
  if (!mechanism) return null;
  const state = ls?.link?.state ?? c.data_source_status;
  return {
    mechanism,
    link_state: state === 'alive' || state === 'down' ? state : 'unknown',
    link_last_seen_at: strOrNull(ls?.link?.last_seen_at),
    telemetry_last_at: strOrNull(ls?.telemetry?.last_telemetry_at) ?? c.last_telemetry_at,
    // The drone's own flag is the source of truth; `reason` is the BE's wording of it.
    telemetry_reason:
      strOrNull(ls?.telemetry?.reason) ??
      (c.networkid_streaming_enabled === false ? 'networkid_streaming_disabled' : null),
    dock: {
      name: strOrNull(ls?.dock?.name),
      online: ls?.dock?.online ?? null,
      aircraft_state: ls?.dock?.aircraft_state ?? 'unknown',
    },
  };
}

export type AircraftBufferKey = 'geofence_buffer_m' | 'obstacle_buffer_m';
export const AIRCRAFT_BUFFER_KEYS: readonly AircraftBufferKey[] = [
  'geofence_buffer_m',
  'obstacle_buffer_m',
];

/** The buffer keys a record actually carries (ENG-295). A key the backend did
 *  not send stays absent — that is how the panel tells "not available on this
 *  tenant yet" (older Custodian) from "not set" (null). */
function bufferFields(
  r: Partial<Record<AircraftBufferKey, number | string | null>>,
): Partial<Record<AircraftBufferKey, number | null>> {
  const out: Partial<Record<AircraftBufferKey, number | null>> = {};
  for (const k of AIRCRAFT_BUFFER_KEYS) {
    if (r[k] !== undefined) out[k] = num(r[k]);
  }
  return out;
}

/** Map a Custodian record → our InventoryDrone. Fields no endpoint carries
 *  stay null/'' and render '—'. */
export function toInventoryDrone(c: CustodianDrone): InventoryDrone {
  const telemetryAgeMs = c.last_telemetry_at
    ? Date.now() - new Date(c.last_telemetry_at).getTime()
    : Number.POSITIVE_INFINITY;
  const dtwinFresh = telemetryAgeMs < 5 * 60_000;
  const ac = (c.adapter_config ?? {}) as Record<string, unknown>;

  return {
    drone_id: c.serial_number,
    display_name: strOrNull(c.callsign) ?? strOrNull(c.name) ?? c.serial_number,
    status: mapStatus(c.status, c.retired_at),
    brand: str(c.brand),
    model: str(c.drone_model),
    drone_type: str(c.drone_type),
    serial_number: c.serial_number,
    registration: strOrNull(c.identification_number),
    firmware_version: str(c.firmware_version),
    hardware_version: str(c.hardware_version),
    weight_kg: specNum(c.weight),
    payload_capacity_kg: numStr(c.payload_capacity),
    // Real DLB airframe maxima now that the list carries them — no longer
    // mirrored from the OI-automation speed defaults.
    max_horizontal_speed: specNum(c.max_horizontal_speed),
    max_vertical_speed: specNum(c.max_vertical_speed),
    max_flight_time_min: flightTimeMinutes(c.max_flight_time),
    personality: personalityLabel(c.adapter_type),
    // "Deployed" = custodian is configured (active/non-retired), independent of
    // whether the adapter is momentarily connected. Live connectivity is
    // reflected by custodian_status (online/offline) below.
    agent_deployed: c.retired_at == null,
    agent_location: null,
    yaml_created: false,
    yaml: '',
    // Only `/config` carries these — see applyConfigToInventoryDrone.
    c2_links: null,
    video_feeds: null,
    notes: strOrNull(c.notes),
    guid: strOrNull(c.guid) ?? c.dlb_guid,
    ec_callsign: strOrNull(c.callsign),
    ec_tc_reg: strOrNull(c.identification_number),
    ec_adsb_hex: null, // — no ADS-B hex field on any endpoint
    adsb_identification_code: strOrNull(c.adsb_identification_code),
    adsb_out: c.adsb_out_enabled ? 'Enabled' : 'Disabled',
    adsb_in: c.adsb_in_enabled ? 'Active' : 'N/A',
    daa_rules: [], // — no endpoint
    propulsion_type: str(c.propulsion_type),
    purchase_date: str(c.purchase_date),
    insurable_value: c.insurable_value == null ? '' : String(c.insurable_value),
    custodian_status: c.retired_at ? 'none' : c.adapter_connected ? 'online' : 'offline',
    custodian_runtime: '', // — not in API
    custodian_configured: true,
    gcs_plugin: strOrNull(c.gcs_plugin) ?? 'none',
    gcs_plugin_status: normalisePluginStatus(c.gcs_plugin_status) ?? 'none',
    adapter_type: c.adapter_type,
    publish_rate_hz: c.publish_rate_hz,
    dtwin_sync: dtwinFresh ? 'updated' : 'outdated',
    dtwin_sync_label: agoLabel(c.last_telemetry_at),
    dji_status: djiStatusFrom(c),
    dtwin_state: {
      adapter_connected: c.adapter_connected,
      last_telemetry_at: c.last_telemetry_at,
      flight_mode: c.flight_mode ?? '',
      armed: c.armed ?? false,
      battery_remaining_pct:
        c.battery_remaining_pct != null && c.battery_remaining_pct >= 0
          ? c.battery_remaining_pct
          : null,
    },
    ownership_flag_enabled: c.ownership_flag_enabled,
    oi_automation_enabled: c.oi_automation_enabled,
    adsb_surrogate_enabled: c.adsb_surrogate_enabled,
    // No Custodian field for the NetworkID gate yet — the feed is live for
    // every aircraft today, so default it on rather than showing every
    // drone as suppressed.
    networkid_streaming_enabled: c.networkid_streaming_enabled ?? true,
    tisb_rebroadcast_enabled: c.tisb_rebroadcast_enabled,
    horizontal_speed_ms: c.horizontal_speed_ms,
    vertical_speed_ms: c.vertical_speed_ms,
    oi_automation_square_m: num(c.oi_automation_square_m),
    oi_automation_radius_m: num(c.oi_automation_radius_m),
    oi_automation_amend_min_trigger_m: num(c.oi_automation_amend_min_trigger_m),
    oi_automation_amend_max_alt_agl_m: num(c.oi_automation_amend_max_alt_agl_m),
    oi_automation_amend_enabled: c.oi_automation_amend_enabled ?? false,
    // Documented default is on; an older Custodian that sends nothing reads as on.
    oi_automation_time_amend_enabled: c.oi_automation_time_amend_enabled ?? true,
    oi_automation_time_amend_trigger_s: num(c.oi_automation_time_amend_trigger_s),
    oi_automation_time_amend_extension_s: num(c.oi_automation_time_amend_extension_s),
    fv_lateral_buffer_m: num(c.fv_buffer_m),
    cv_lateral_buffer_m: num(c.cv_buffer_m),
    waypoint_buffer_m: num(c.waypoint_buffer_m),
    ...bufferFields(c),
    // — no Custodian endpoint carries pilot or separation defaults yet.
    default_pilot_name: null,
    default_pilot_fs_guid: null,
    nmac_distance_m: null,
    swc_distance_m: null,
    nmac_h_crewed_m: null,
    nmac_v_crewed_m: null,
    nmac_h_uncrewed_m: null,
    nmac_v_uncrewed_m: null,
    swc_h_crewed_m: null,
    swc_v_crewed_m: null,
    swc_h_uncrewed_m: null,
    swc_v_uncrewed_m: null,
    eval_distance_m: null,
    data_source: deriveDataSource(c),
    data_source_max_latency_ms: null,
    adsb_observed: null, // — no surveillance-observation endpoint
    adsb_out_device: strOrNull(ac.adsb_out_device),
    adsb_hex_mode: 'dynamic',
    surveillance_mode: '', // — only on /config
    color: str(c.color),
    tech_number: str(c.tech_number),
    company_guid: strOrNull(c.company_guid),
    user_guid: strOrNull(c.user_guid),
    flight_controller_serial: str(c.flight_controller_serial_number),
    link_rssi_dbm: num(c.link_rssi_dbm),
    adapter_host: strOrNull(ac.host),
    adapter_port: num(ac.port),
    adapter_protocol: strOrNull(ac.protocol) ?? strOrNull(c.protocol),
  };
}
